import React, { useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import ProductFeaturesMenu from './ProductFeaturesMenu';

/**
 * 导航链接类型定义
 */
interface NavItem {
  text: string;
  href: string;
}

/**
 * 产品功能项接口
 */
interface ProductFeature {
  id: string;
  title: string;
  description: string;
  iconName?: string;
}

/**
 * 产品功能分类接口
 */
interface ProductFeatureCategory {
  id: string;
  title: string;
  features: ProductFeature[];
}

/**
 * 导航栏组件
 * @returns {JSX.Element} 渲染的导航栏组件
 */
const Navbar: React.FC = () => {
  // 导航链接数据
  const navLinks: NavItem[] = [
    { text: '首页', href: '/' },
    { text: '产品功能', href: '/features' },
    // { text: '行业方案', href: '/solutions' },
    { text: '价格套餐', href: '/pricing' },
    { text: '客户案例', href: '/case-studies' },
    { text: '常见问题', href: '/faq' },
    { text: '联系我们', href: '/contact' }
  ];

  // 产品功能分类数据
  const productFeatureCategories: ProductFeatureCategory[] = [
    {
      id: 'core',
      title: '核心功能',
      features: [
        {
          id: '01',
          title: '智能分析',
          description: '利用AI技术分析用户数据，提供个性化推荐和洞察',
          iconName: '📊'
        },
        {
          id: '02',
          title: '多端同步',
          description: '无缝同步您的数据到所有设备，随时随地访问和管理',
          iconName: '🔄'
        },
        {
          id: '03',
          title: '安全加密',
          description: '采用先进的加密技术保护您的数据安全',
          iconName: '🔒'
        }
      ]
    },
    {
      id: 'advanced',
      title: '高级功能',
      features: [
        {
          id: '04',
          title: '自动化工作流',
          description: '设置自定义规则和触发器，自动执行重复性任务',
          iconName: '⚙️'
        },
        {
          id: '05',
          title: '团队协作',
          description: '支持多人实时协作，共享文档和资源',
          iconName: '👥'
        },
        {
          id: '06',
          title: '高级报表',
          description: '生成详细的数据报表和可视化图表',
          iconName: '📈'
        }
      ]
    },
    {
      id: 'integration',
      title: '集成与扩展',
      features: [
        {
          id: '07',
          title: 'API接口',
          description: '提供强大的API接口，允许开发者构建自定义集成',
          iconName: '🔌'
        },
        {
          id: '08',
          title: '第三方集成',
          description: '与常用工具和服务集成，简化工作流程',
          iconName: '🔗'
        },
        {
          id: '09',
          title: '插件市场',
          description: '访问丰富的插件市场，根据需求扩展产品功能',
          iconName: '🧩'
        }
      ]
    }
  ];
  // 移动端菜单状态
  const [mobileMenuOpen, setMobileMenuOpen] = useState<boolean>(false);
  // 移动端产品功能子菜单状态
  const [mobileFeatureMenuOpen, setMobileFeatureMenuOpen] = useState<boolean>(false);

  /**
   * 切换移动端菜单显示状态
   */
  const toggleMobileMenu = (): void => {
    setMobileMenuOpen(!mobileMenuOpen);
    // 关闭子菜单
    if (!mobileMenuOpen) {
      setMobileFeatureMenuOpen(false);
    }
  };

  /**
   * 切换移动端产品功能子菜单显示状态
   */
  const toggleMobileFeatureMenu = (e: React.MouseEvent): void => {
    e.preventDefault();
    e.stopPropagation();
    setMobileFeatureMenuOpen(!mobileFeatureMenuOpen);
  };

  return (
    <nav className="bg-white py-4 px-6 border-b border-gray-100 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto flex justify-between items-center">
        <Link to="/" className="flex items-center">
          <img src="/assets/images/logo.jpg" alt="佳达掌柜 Logo" className="h-8 mr-2" />
          <span className="text-2xl font-bold text-primary">佳达掌柜</span>
        </Link>

        {/* 桌面端导航 */}
        <div className="hidden lg:flex items-center space-x-8">
          {navLinks.map((link, index) => (
            <NavLink
              key={index}
              to={link.href}
              className={({ isActive }) =>
                isActive
                  ? "text-primary font-medium"
                  : "text-gray-600 hover:text-primary transition-colors"
              }
            >
              {link.text}
            </NavLink>
          ))}
        </div>

        {/* 登录/注册按钮 */}
        <div className="hidden lg:flex items-center space-x-4">
          <Link
            to="/login"
            className="text-gray-600 hover:text-primary transition-colors"
          >
            登录
          </Link>
          <Link
            to="/register"
            className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary-dark transition-colors"
          >
            免费注册
          </Link>
        </div>

        {/* 移动端菜单按钮 */}
        <button
          type="button"
          className="lg:hidden text-gray-600 hover:text-primary"
          onClick={toggleMobileMenu}
          aria-expanded={mobileMenuOpen}
        >
          {mobileMenuOpen ? (
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          ) : (
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 6h16M4 12h16M4 18h16"
              />
            </svg>
          )}
        </button>
      </div>

      {/* 移动端菜单 */}
      {mobileMenuOpen && (
        <div className="lg:hidden mt-4 py-4 px-4 bg-white border-t border-gray-100">
          <div className="flex flex-col space-y-4">
            {navLinks.map((link, index) => (
              <NavLink
                key={index}
                to={link.href}
                className={({ isActive }) =>
                  isActive
                    ? "text-primary font-medium"
                    : "text-gray-600 hover:text-primary transition-colors"
                }
                onClick={() => setMobileMenuOpen(false)}
              >
                {link.text}
              </NavLink>
            ))}
            <div className="relative">
              <button
                className="flex items-center justify-between w-full text-gray-600 hover:text-primary transition-colors"
                onClick={toggleMobileFeatureMenu}
                aria-expanded={mobileFeatureMenuOpen}
              >
                <span>产品功能</span>
                <svg
                  className={`w-4 h-4 transition-transform duration-200 ${
                    mobileFeatureMenuOpen ? "transform rotate-180" : ""
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>
              <ProductFeaturesMenu
                productFeatureCategories={productFeatureCategories}
                isMobile={true}
                mobileFeatureMenuOpen={mobileFeatureMenuOpen}
                toggleMobileFeatureMenu={toggleMobileFeatureMenu}
                closeMobileMenu={() => setMobileMenuOpen(false)}
              />
            </div>
            <div className="pt-4 mt-4 border-t border-gray-100 flex flex-col space-y-4">
              <Link
                to="/login"
                className="text-gray-600 hover:text-primary transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                登录
              </Link>
              <Link
                to="/register"
                className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary-dark transition-colors text-center"
                onClick={() => setMobileMenuOpen(false)}
              >
                免费注册
              </Link>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;